import {
  DateFormatOptions,
  PhoneNumberFormatOptions,
  CurrencyFormatOptions,
  CurrencyDecimalOptions,
} from "block-system/blocks/Form/Field/schema";
import { create as createFieldBlock } from "block-system/blocks/Form/Field/operations";
import {
  FieldBlock,
  FieldBlockConfig,
  FieldBlockDropDownConfig,
} from "block-system/blocks/Form/Field/types";
import cuid from "cuid";
import { APIField } from "server/services/tables/types/fields";
import { slugify } from "utils/slugify";

/**
 * Converts a field from Tables into a Form Field for a form in interfaces.
 * If a table field type that is not supported is encountered, we skip it.
 */
export function tableFieldToFormField(
  /**
   * TODO: Remove the APIField.
   * We no longer return this shape from the backend.
   * https://zapierorg.atlassian.net/browse/INTRFCS-2866
   */
  tableField: APIField,
  tableId: string | undefined
): FieldBlock | null {
  // Locked fields can be displayed in a table but not in form
  if (isLockedField(tableField)) {
    return null;
  }

  const initialConfig: Partial<FieldBlockConfig> = {
    id: cuid(),
    name: slugify(tableField.name),
    label: tableField.name,
    nameAutoGenerated: true,
    required: tableField.options?.required,
    tablesFieldKey: `f${tableField.id}`,
  };

  switch (tableField.type) {
    case "formula":
      initialConfig.inputType = "formula";
      initialConfig.readOnly = true;
      break;
    case "ai_formula":
      initialConfig.inputType = "ai-formula";
      initialConfig.readOnly = true;
      break;
    case "linked_record":
      initialConfig.inputType = "linked-record";
      initialConfig.readOnly = true;
      break;
    case "multiple_linked_record":
      initialConfig.inputType = "multiple-linked-record";
      initialConfig.readOnly = true;
      break;
    case "string": // present in tablesui
      initialConfig.inputType = "text";
      if (tableField.options?.static_choices) {
        Object.assign(initialConfig, {
          ...dropdownFromTableField(tableField, initialConfig),
        });
      } else if (
        tableId &&
        tableField.options?.choice_provider?.kind === "table"
      ) {
        // Handle table field options
        Object.assign(initialConfig, {
          ...dropdownFromDynamicTableField(tableField, tableId, initialConfig),
        });
      }
      break;
    case "labeled_string":
      initialConfig.inputType = "text";
      if (tableField.options?.static_choices) {
        Object.assign(initialConfig, {
          ...dropdownFromTableField(tableField, initialConfig),
        });
      } else if (
        tableId &&
        tableField.options?.choice_provider?.kind === "table"
      ) {
        Object.assign(initialConfig, {
          ...dropdownFromDynamicTableField(tableField, tableId, initialConfig),
        });
      }
      break;
    case "multiple_string":
    case "multiple_labeled_string": // present in tablesui as "Dropdown" with multi select
      initialConfig.inputType = "text";
      if (tableField.options?.static_choices) {
        // Don't try to convert to a dropdown unless we have the choices available
        Object.assign(initialConfig, {
          ...dropdownFromTableField(tableField, initialConfig),
        });
      } else if (
        tableId &&
        tableField.options?.choice_provider?.kind === "table"
      ) {
        Object.assign(initialConfig, {
          ...dropdownFromDynamicTableField(tableField, tableId, initialConfig),
        });
      }
      break;
    case "text": // present in tablesui
      initialConfig.inputType = "textarea";
      break;
    case "boolean":
      initialConfig.inputType = "checkbox";
      if (initialConfig.inputType === "checkbox") {
        initialConfig.text = tableField.name;
      }
      break;
    case "number":
      initialConfig.inputType = "number";
      break;
    case "decimal":
      initialConfig.inputType = "number";
      break;
    case "email":
      initialConfig.inputType = "email";
      break;
    case "link":
      initialConfig.inputType = "url";
      break;
    case "multiple_link":
      initialConfig.inputType = "multi-url";
      break;
    case "datetime": // present in tablesui
      initialConfig.inputType = "date-picker";
      if (initialConfig.inputType !== "date-picker") throw "type guard";
      initialConfig.dateFormat =
        tableField.options?.date_format ?? DateFormatOptions[0].value;
      initialConfig.includeTime = tableField.options?.include_time ?? false;
      break;
    case "phone_number":
      initialConfig.inputType = "phone-number";
      if (initialConfig.inputType !== "phone-number") throw "type guard";
      initialConfig.phoneNumberFormat =
        tableField.options?.phone_number_format ??
        PhoneNumberFormatOptions[0].value;
      break;
    case "currency":
      initialConfig.inputType = "currency";
      if (initialConfig.inputType !== "currency") throw "type guard";
      initialConfig.currencyFormat =
        tableField.options?.currency_format ?? CurrencyFormatOptions[0].value;
      initialConfig.decimals =
        tableField.options?.decimals ?? CurrencyDecimalOptions[0].value;
      break;
    /**
     * Tables only
     */
    case "button_trigger_zap":
    case "button_continue_zap":
      return null;
    /**
     * Not yet implemeneted
     */
    case "multiple_text":
    case "multiple_boolean":
    case "multiple_number":
    case "multiple_decimal":
    case "multiple_datetime":
    case "uuid":
    case "multiple_uuid":
    case "json":
    case "multiple_json":
    case "multiple_email":
    default:
      throw new Error(
        `Unsupported field: ${tableField.type}; field: ${tableField.name}`
      );
  }

  // We need an early return because linked record table field types have static choices and choice provider, just like dropdowns.
  // We don't want them treated by other logic meant to catch dropdowns.
  if (["linked_record", "multiple_linked_record"].includes(tableField.type)) {
    return {
      type: "field-block",
      config: createFieldBlock({ initialConfig }).config,
    };
  }

  if (tableField.options?.static_choices) {
    // This will always convert into a dropdown
    Object.assign(initialConfig, {
      ...dropdownFromTableField(tableField, initialConfig),
    });
  }

  if (tableId && tableField.options?.choice_provider?.kind === "table") {
    Object.assign(initialConfig, {
      ...dropdownFromDynamicTableField(tableField, tableId, initialConfig),
    });
  }

  return {
    type: "field-block",
    config: createFieldBlock({ initialConfig }).config,
  };
}

function dropdownFromTableField(
  tableField: APIField,
  initialConfig: Partial<FieldBlockConfig>
): Partial<FieldBlockDropDownConfig> {
  // TODO: [Seth]  can we handle choice provider somehow?
  //       [Peter] ^^ yes and we should. But it'll likely take a refactor of
  //               components or endpoints.
  if (!tableField.options?.static_choices)
    throw new Error("Expected static_choices defined on table field");

  return createFieldBlock({
    initialConfig: {
      ...initialConfig,
      inputType: "dropdown",
      multiSelect:
        tableField.type === "multiple_labeled_string" ||
        tableField.type === "multiple_string",
      options: tableField.options.static_choices.map((c) => c.value).join("\n"),
    },
  }).config as FieldBlockDropDownConfig;
}

function dropdownFromDynamicTableField(
  tableField: APIField,
  tableId: string,
  initialConfig: Partial<FieldBlockConfig>
): Partial<FieldBlockDropDownConfig> {
  if (tableField.options?.choice_provider?.kind !== "table")
    throw new Error("Expected a table data source");

  return createFieldBlock({
    initialConfig: {
      ...initialConfig,
      inputType: "dropdown",
      multiSelect:
        tableField.type === "multiple_labeled_string" ||
        tableField.type === "multiple_string",
      tableId: tableId,
      tableFieldId: tableField.id,
    },
  }).config as FieldBlockDropDownConfig;
}

function isLockedField(tableField: APIField) {
  const tableFieldOptions = tableField.options ?? {};

  /**
   * The `locked` most likely do not exist on `options`
   * since we no longer get `APIField` from the backend, but rather `APIFieldLatest`.
   * TODO: https://zapierorg.atlassian.net/browse/INTRFCS-2866
   */
  if ("locked" in tableFieldOptions) {
    return tableFieldOptions.locked ?? false;
  }

  if ("config" in tableField) {
    return tableField.config?.locked ?? false;
  }
}
